// Time picker as a list of 30-minute slots (00:00 … 23:30), 24h. Opens a
// scrollable dropdown that lands on the current value; no <input type=time>.
//
// The list is a portaled Popover: inside the schedule drawer it would otherwise
// be clipped by the scroll container, and as an in-flow block it stretched its
// grid cell and pushed the rest of the form down.

import { memo, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Popover } from '../ui/Popover';
import { IconChevronDown, IconClock } from '../ui/icons';

const SLOTS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  return `${String(h).padStart(2, '0')}:${m}`;
});

export const TimeSelect = memo(function TimeSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selRef = useRef<HTMLButtonElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const toggle = useCallback(() => setOpen((o) => !o), []);
  const close = useCallback(() => setOpen(false), []);
  const pick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const t = e.currentTarget.dataset.t;
      if (t) onChange(t);
      setOpen(false);
    },
    [onChange],
  );

  // Land the open list on the selected time (nearest = inside the list only,
  // so it never scrolls the page behind the popover).
  useEffect(() => {
    if (open) selRef.current?.scrollIntoView({ block: 'center' });
  }, [open]);

  const slots = useMemo(() => SLOTS, []);

  return (
    <div className="bz-timesel">
      <button
        ref={btnRef}
        type="button"
        className="bz-timesel-btn"
        onClick={toggle}
        aria-expanded={open}
      >
        <IconClock size={15} />
        <span className="bz-timesel-val">{value}</span>
        <IconChevronDown size={13} />
      </button>
      {open && (
        <Popover anchor={btnRef.current} onClose={close} matchWidth className="bz-timesel-list">
          <div role="listbox">
            {slots.map((t) => {
              const on = t === value;
              return (
                <button
                  key={t}
                  ref={on ? selRef : null}
                  type="button"
                  className={on ? 'bz-timesel-opt bz-timesel-opt-on' : 'bz-timesel-opt'}
                  data-t={t}
                  onClick={pick}
                  aria-selected={on}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </Popover>
      )}
    </div>
  );
});
